import { FORMAT_LABELS, type Format, FormatSchema } from '@ugin/shared';
import { BarChart3 } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';

import { type WidgetComponent, type WidgetMeta } from '../_base/types';
import { WidgetContainer } from '../_base/WidgetContainer';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMeta } from '@/hooks/useMeta';
import { cn } from '@/lib/utils';


const meta: WidgetMeta = {
  id: 'meta-share',
  name: 'Meta share',
  description: "What's actually being played, by archetype.",
  icon: BarChart3,
  defaultSize: { w: 6, h: 4 },
  category: 'meta',
};

export interface MetaShareProps {
  initialFormat?: Format;
  /** If provided, the widget runs in controlled mode (parent owns the format). */
  format?: Format;
  onFormatChange?: (f: Format) => void;
  compact?: boolean;
}

export const MetaShareWidget: WidgetComponent<MetaShareProps> = ({
  initialFormat = 'standard',
  format: controlledFormat,
  onFormatChange,
  compact = false,
}) => {
  const [internalFormat, setInternalFormat] = useState<Format>(initialFormat);
  const format = controlledFormat ?? internalFormat;
  const setFormat = (f: Format) => {
    if (controlledFormat === undefined) setInternalFormat(f);
    onFormatChange?.(f);
  };
  // A parent that pins the format and offers no handler must not render a
  // second control labelled Format that silently does nothing.
  const ownsFormat = controlledFormat === undefined || onFormatChange !== undefined;
  const { data, isLoading, error, refetch } = useMeta(format);
  const archetypes = data?.archetypes ?? [];
  const top = archetypes.slice(0, compact ? 5 : 12);
  const max = top.reduce((m, a) => Math.max(m, a.share), 0);

  return (
    <WidgetContainer
      meta={meta}
      icon={<BarChart3 className="size-4" />}
      actions={
        ownsFormat ? (
          <Select value={format} onValueChange={(v) => { setFormat(v as Format); }}>
            <SelectTrigger className="h-8 w-36 text-xs" aria-label="Format">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FormatSchema.options.map((f) => (
                <SelectItem key={f} value={f}>
                  {FORMAT_LABELS[f]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null
      }
      loading={isLoading}
      error={error ?? undefined}
      onRetry={() => void refetch()}
      empty={!isLoading && top.length === 0}
      emptyTitle={`No ${FORMAT_LABELS[format]} meta snapshot yet`}
      emptyDescription="Run the meta-scraper or wait for the next morning sync. Ugin will catch up."
    >
      <AnimatePresence mode="wait">
        <motion.ul
          key={format}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="space-y-2"
        >
          {top.map((arch, idx) => (
            <motion.li
              key={arch.name}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: Math.min(idx * 0.025, 0.2) }}
              className="grid grid-cols-[1fr_auto] gap-3 text-sm"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-ugin-text">{arch.name}</span>
                  <span className="font-mono text-xs text-ugin-text-muted">
                    {arch.share.toFixed(1)}%
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-ugin-bg-deep">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(arch.share / Math.max(max, 1)) * 100}%` }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className={cn('h-full rounded-full bg-gradient-to-r from-ugin-accent-soft to-ugin-accent')}
                  />
                </div>
              </div>
            </motion.li>
          ))}
        </motion.ul>
      </AnimatePresence>
      {data?.sources.length ? (
        <p className="mt-4 text-xs text-ugin-text-faint">
          Sources: {data.sources.map((s) => s.source).join(', ')}
        </p>
      ) : null}
    </WidgetContainer>
  );
};

MetaShareWidget.meta = meta;
